import { GridCellParams } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import Chip from '@mui/material/Chip';
import { Fragment, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { BehaviorSubject } from 'rxjs';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { UpstackDataGrid } from '../UpstackDataGrid/UpstackDataGrid';
import { Icon } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { Asset } from '../../../store/Asset';
import { addDrawer } from '../../../store/Drawer';
import { ComponentName, getMetaData } from '../DetailsDrawer/detailsDrawersHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { GridCustomColDef } from '../../types';
import { chipStyles, formatPercent, formatPrice, getDate, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { isData, isObserver } from '../../../store/User';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../store/GeneralStore';
import { RecordLink } from '../../Atoms/RecordLink';

const handleAssetClick = (row: Asset) => {
  addDrawer({
    id: v4(),
    data: row,
    ...getMetaData({ name: ComponentName.ASSET, sfId: row.sf_id || '', id: row.id, data: row })
  });
};

export const allCols: GridCustomColDef[] = [
  {
    headerName: 'Number',
    field: 'number',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <button
        className="flex items-center"
        onClick={() => handleAssetClick(params.row)}>
        <Icon
          className="p-3 mr-1"
          type="assetMed"
        />
        <Text
          size="sm14"
          color="indigo">
          {params?.row.number as string}
        </Text>
      </button>
    )
  },
  {
    headerName: 'Comm. Status',
    field: 'commissionsStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    flex: 1,
    type: 'singleSelect',
    filterable: false,
    valueOptions: []
  },
  {
    headerName: 'Svc. Status',
    field: 'assetStatus',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false
  },
  {
    headerName: 'Exp. First Run',
    field: 'expectedFirstCommissionRun',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    }
  },
  {
    headerName: 'Act. First Run',
    field: 'actualFirstCommissionRun',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    filterable: false,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    }
  },
  {
    headerName: 'Supplier',
    field: 'supplierName',
    flex: 1,
    type: 'singleSelect',
    filterable: false,
    valueOptions: []
  },
  {
    headerName: 'Customer',
    field: 'customer',
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <RecordLink to={`/customer/${params.row.accountGri}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </RecordLink>
    ),
    flex: 1,
    type: 'singleSelect',
    valueOptions: []
  },
  {
    headerName: 'Comm. MRC',
    field: 'commissionableMRC',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    headerAlign: 'right',
    align: 'right',
    filterable: false,
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    headerName: 'Recurring %',
    field: 'recurringCommissionPercent',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    headerAlign: 'right',
    align: 'right',
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    ),
    valueFormatter: (value) => value
  },
  {
    headerName: 'Gross MRR',
    field: 'grossMRR',
    flex: 1,
    type: 'singleSelect',
    valueOptions: [],
    headerAlign: 'right',
    align: 'right',
    filterable: false,
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  }
];

export interface AssetMiniTableParams {
  id: string;
  fetchFunction: (
    id: string,
    forDrawer?: boolean
  ) => (filters?: ApiAdvancedFilter) => Promise<PaginatedResponse<Asset> | undefined | void>;
  removeFunction?: (id: string) => Promise<void>;
  showRemove?: boolean;
  dataSubject: () => BehaviorSubject<PaginatedResponse<Asset>>;
  loadingSubject: () => BehaviorSubject<boolean>;
  type?: string;
  drawer?: boolean;
}

export const AssetMiniTable = ({
  id,
  fetchFunction,
  showRemove,
  removeFunction,
  dataSubject,
  loadingSubject,
  type,
  drawer = false
}: AssetMiniTableParams) => {
  const [assets, setAssets] = useState<PaginatedResponse<Asset>>({} as PaginatedResponse<Asset>);
  const [loading, setLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const { setSnack } = useSnackbar();

  const handleRemove = async (row: Asset) => {
    const assetId = type === 'Reference' ? row.id : row.linkId;
    if (removeFunction && showRemove && assetId) {
      await removeFunction(assetId);
      setSnack({ message: `${type || 'Link'} removed`, type: 'success', open: true });
      fetchFunction(id, drawer)();
    }
  };

  useEffect(() => {
    const dataSub = dataSubject().subscribe((r) => {
      if (!tableLoading) setAssets(r);
      setLoading(false);
    });

    const loadingSub = loadingSubject().subscribe((loading) => setTableLoading(loading));

    return () => {
      if (dataSub) dataSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [tableLoading]);

  useEffect(() => {
    if (id) fetchFunction(id, drawer)();
  }, [id, drawer]);

  const drawerHiddenFields = ['actualFirstCommissionRun', 'supplierName', 'customer', 'recurringCommissionPercent'];
  const tableCols: GridCustomColDef[] = [
    ...(drawer ? allCols.filter((col) => !drawerHiddenFields.includes(col.field)) : allCols)
  ];

  if (!isObserver() && !isData())
    tableCols.push({
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      type: 'singleSelect',
      valueOptions: [],
      sortable: false,
      renderCell: (params) => (
        <Fragment>
          {showRemove && (
            <button
              className="-mt-[0.125rem] align-top"
              onClick={() => handleRemove(params.row)}>
              <DeleteIcon color="buttonGrey" />
            </button>
          )}
        </Fragment>
      )
    });

  const sxStyle = {
    ...DEFAULT_MINI_TABLE_SX_STYLES,
    minHeight: 'inherit',
    height: 'inherit'
  };

  return (
    <div
      className="w-full"
      data-testid="asset-mini-table-container">
      {loading && <ComponentSpinner label="Loading Asset Data" />}
      {!loading && (
        <UpstackDataGrid
          rows={assets.data || []}
          sxStyles={sxStyle}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
            sorting: { sortModel: [{ field: 'number', sort: 'desc' }] }
          }}
          currentPage={assets.page}
          rowCount={assets.totalCount || 0}
          columns={tableCols}
          dataCy="asset-mini-data-grid-pagination"
          fetchFunction={fetchFunction(id, drawer)}
          title="asset"
          showSearch={false}
          page="assetGrid_MiniTable"
          apiFilter
          gridSettings={{ muiConfig: {}, disabledFields: [] }}
          pageSizeOptions={[50]}
          showToolbar={false}
          withoutSave
          localLoading={tableLoading}
          pagination={assets.totalPages > 1}
        />
      )}
    </div>
  );
};
